// First person view of the 3D city, as plain math so it can be tested without a GPU.
// Map heading h points along (cos h, sin h) in map x and y, which is world X and Z (see isoCamera.ts). A cell's middle is
// at +0.5. Pitch is radians above the horizon.
import { useSyncExternalStore } from 'react'
import type { TierSpec } from './quality'

export type ViewMode = 'first' | 'iso'
export type V3 = readonly [number, number, number]

/** Eye height as a share of the figure's drawn height (a 3D figure is 1.22 per unit of avatar height, eyes just under the crown). */
export const EYE_K = 1.12
export const PITCH_MAX = (70 * Math.PI) / 180, PITCH_MIN = -PITCH_MAX
/** Radians of turn per pixel of a drag, and radians per second of a held turn key. */
export const LOOK_PER_PX = 0.0055, TURN_RATE = (120 * Math.PI) / 180
/** How far ahead (cells) a held forward key sets the destination, and back. */
export const FORWARD_CELLS = 4, BACK_CELLS = 2
/** The size the iso view is held at while the first person camera is up: shadows and the culling radius read it. */
export const FIRST_VIEW_ZOOM = 1.6
export const NEAR = 0.05

export const eyeHeight = (avatarHeight: number): number => EYE_K * Math.max(0.2, avatarHeight)
export const clampPitch = (p: number): number => Math.min(PITCH_MAX, Math.max(PITCH_MIN, Number.isFinite(p) ? p : 0))
/** A yaw wrapped into [-PI, PI). */
export function wrapYaw(y: number): number {
  if (!Number.isFinite(y)) return 0
  const t = Math.PI * 2, w = ((y + Math.PI) % t + t) % t
  return w - Math.PI
}
/** A look turned by a drag of (dx, dy) pixels. As a grab (both inverts off): dragging right turns the view left, dragging
 *  down looks up. `invertX` turns the view the way the drag goes, `invertY` looks down when dragging down. */
export function dragLook(look: { yaw: number; pitch: number }, dx: number, dy: number, o: { invertX?: boolean; invertY?: boolean } = {}, k = LOOK_PER_PX): { yaw: number; pitch: number } {
  const sx = o.invertX ? -1 : 1, sy = o.invertY ? -1 : 1
  return { yaw: wrapYaw(look.yaw - sx * dx * k), pitch: clampPitch(look.pitch + sy * dy * k) }
}

export interface Held { fwd: boolean; back: boolean; left: boolean; right: boolean }
export const noHeld = (): Held => ({ fwd: false, back: false, left: false, right: false })
/** +1 walking forward, -1 back, 0 standing (both keys cancel). */
export const moveIntent = (h: Held): -1 | 0 | 1 => (h.fwd === h.back ? 0 : h.fwd ? 1 : -1)
/** Cells a sideways step reaches ahead of the walk (working default, like FORWARD_CELLS). */
export const STRAFE_CELLS = 3
/**
 * Where held keys walk (Jared 11/3: the mouse turns, so left and right strafe): forward or back along the look, left or
 * right square to it, or a diagonal of both. Yaw grows toward the right, so a right step is yaw + 90 degrees.
 * Null when nothing moves (opposite keys cancel).
 */
export function moveHeading(h: Held, yaw: number): { heading: number; cells: number } | null {
  const f = moveIntent(h), side = (h.right ? 1 : 0) - (h.left ? 1 : 0)
  if (!f && !side) return null
  return { heading: wrapYaw(yaw + Math.atan2(side, f)), cells: f > 0 ? FORWARD_CELLS : f < 0 ? BACK_CELLS : STRAFE_CELLS }
}
/** The yaw after `dt` seconds of held turn keys (right turns toward +z when facing +x, so yaw grows). */
export function stepYaw(yaw: number, h: Held, dt: number): number {
  const d = (h.right ? 1 : 0) - (h.left ? 1 : 0)
  return d ? wrapYaw(yaw + d * TURN_RATE * Math.min(dt, 0.1)) : yaw
}

/** Whether the left mouse button (or a finger) is down on the map in first person: looking round. While it is, the walk
 *  destination ring is not drawn (Jared 11/3). */
export const firstPointer = { down: false }
/** The look and the held keys: written by input, read by the camera each frame. Nothing here causes a React render. */
export const firstLook = { yaw: 0, pitch: 0 }
export const firstHeld: Held = noHeld()
/** Pad and key presses can overlap (a finger and a key): each source holds its own bit. */
export function setHeld(source: string, key: keyof Held, on: boolean): void {
  const s = sources.get(source) ?? noHeld(); s[key] = on; sources.set(source, s)
  for (const k of ['fwd', 'back', 'left', 'right'] as const) { let any = false; for (const v of sources.values()) any ||= v[k]; firstHeld[k] = any }
}
const sources = new Map<string, Held>()
export function releaseAll(): void { sources.clear(); Object.assign(firstHeld, noHeld()) }

export interface FirstView { x: number; y: number; z: number; yaw: number; pitch: number; fov: number; w: number; h: number }
/** The vertical field of view for a screen: wider on a tall phone, where the sides are narrow. */
export const fovFor = (w: number, h: number): number => (h > w ? 78 : 66)
/** The eye for a player drawn at map (x, y) standing at world height `standY`. */
export function eyeView(at: { x: number; y: number }, standY: number, avatarHeight: number, yaw: number, pitch: number, w: number, h: number): FirstView {
  return { x: at.x + 0.5, y: standY + eyeHeight(avatarHeight), z: at.y + 0.5, yaw, pitch: clampPitch(pitch), fov: fovFor(w, h), w, h }
}
export function lookDir(yaw: number, pitch: number): V3 {
  const c = Math.cos(pitch)
  return [Math.cos(yaw) * c, Math.sin(pitch), Math.sin(yaw) * c]
}
/** The point the camera looks at (one unit along the look). */
export function lookTarget(v: FirstView): V3 { const d = lookDir(v.yaw, v.pitch); return [v.x + d[0], v.y + d[1], v.z + d[2]] }
const cross = (a: V3, b: V3): V3 => [a[1] * b[2] - a[2] * b[1], a[2] * b[0] - a[0] * b[2], a[0] * b[1] - a[1] * b[0]]
const norm = (a: V3): V3 => { const l = Math.hypot(a[0], a[1], a[2]) || 1; return [a[0] / l, a[1] / l, a[2] / l] }
/** Screen right and up, and forward, in world space. */
function basis(v: FirstView): { f: V3; r: V3; u: V3 } {
  const f = lookDir(v.yaw, v.pitch), r = norm(cross(f, [0, 1, 0])), u = cross(r, f)
  return { f, r, u }
}
/** Pixels per world unit at distance `depth` straight ahead. */
export const pxAtDepth = (v: FirstView, depth: number): number => (v.h / 2) / Math.tan((v.fov * Math.PI) / 360) / Math.max(NEAR, depth)

/** Where a world point lands on screen, or null when it is behind the eye. */
export function projectFirst(v: FirstView, x: number, y: number, z: number): { sx: number; sy: number; depth: number } | null {
  const { f, r, u } = basis(v), d: V3 = [x - v.x, y - v.y, z - v.z]
  const depth = d[0] * f[0] + d[1] * f[1] + d[2] * f[2]
  if (depth <= NEAR) return null
  const k = (v.h / 2) / Math.tan((v.fov * Math.PI) / 360)
  return { sx: v.w / 2 + ((d[0] * r[0] + d[1] * r[1] + d[2] * r[2]) / depth) * k, sy: v.h / 2 - ((d[0] * u[0] + d[1] * u[1] + d[2] * u[2]) / depth) * k, depth }
}
/** The unit direction of the ray through a screen pixel. */
export function rayFirst(v: FirstView, sx: number, sy: number): V3 {
  const { f, r, u } = basis(v), k = (v.h / 2) / Math.tan((v.fov * Math.PI) / 360)
  const a = (sx - v.w / 2) / k, b = -(sy - v.h / 2) / k
  return norm([f[0] + r[0] * a + u[0] * b, f[1] + r[1] * a + u[1] * b, f[2] + r[2] * a + u[2] * b])
}
/** The point on the horizontal plane at `planeY` under a pixel (world X and Z), or null when the ray misses it or lands beyond `maxDist`. */
export function groundFirst(v: FirstView, sx: number, sy: number, planeY: number, maxDist = 60): { x: number; z: number } | null {
  const d = rayFirst(v, sx, sy)
  if (Math.abs(d[1]) < 1e-6) return null
  const t = (planeY - v.y) / d[1]
  if (t <= 0 || t > maxDist) return null
  return { x: v.x + d[0] * t, z: v.z + d[2] * t }
}
/** Distance on the ground (cells) from the eye to a map cell, for ordering what is under a tap (nearer is in front) and culling. */
export const groundDist = (v: FirstView, x: number, y: number): number => Math.hypot(x + 0.5 - v.x, y + 0.5 - v.z)

/**
 * The cell a held forward (or back) key walks to: up to `cells` cells along the heading, stopping short of the first
 * cell that is not open, so a wall ends the walk instead of sending the server round it. Null when the first step is blocked.
 */
export function forwardDest(from: { x: number; y: number }, heading: number, cells: number, open: (x: number, y: number) => boolean): { x: number; y: number } | null {
  const sx = Math.round(from.x), sy = Math.round(from.y)
  let best: { x: number; y: number } | null = null
  for (let n = 1; n <= cells; n++) {
    const x = Math.round(from.x + Math.cos(heading) * n), y = Math.round(from.y + Math.sin(heading) * n)
    if (x === sx && y === sy) continue
    if (!open(x, y)) break
    best = { x, y }
  }
  return best
}

/** The map rectangle to keep loaded: a box of half-width `radius` centred a little ahead of the player, so the city streams in the look direction. */
export function lookRect(at: { x: number; y: number }, yaw: number, radius: number): { x0: number; y0: number; x1: number; y1: number } {
  const cx = at.x + Math.cos(yaw) * radius * 0.4, cy = at.y + Math.sin(yaw) * radius * 0.4
  return { x0: Math.floor(cx - radius), y0: Math.floor(cy - radius), x1: Math.ceil(cx + radius), y1: Math.ceil(cy + radius) }
}
/** How far (cells) the first person view draws and streams, by quality tier. */
export const viewRadius = (tier: Pick<TierSpec, 'name'>): number => (tier.name === 'low' ? 20 : tier.name === 'medium' ? 28 : 36)

// ---- which view: ?view=first, else the device's saved choice, else overhead ----
const KEY = 'city.view'
export function parseView(search: string): ViewMode | null {
  const v = new URLSearchParams(search).get('view')
  return v === 'first' || v === 'iso' ? v : v === 'overhead' ? 'iso' : null
}
export function withView(search: string, mode: ViewMode): string {
  const p = new URLSearchParams(search)
  if (mode === 'first') p.set('view', 'first'); else p.delete('view')
  const s = p.toString()
  return s ? `?${s}` : ''
}
const read = (): ViewMode | null => { try { const v = localStorage.getItem(KEY); return v === 'first' || v === 'iso' ? v : null } catch { return null } }
const write = (m: ViewMode): void => { try { localStorage.setItem(KEY, m) } catch { /* private mode */ } }
/** The starting view: the address wins, then what this device chose last, then the overhead view. */
export function initialView(search: string, saved: ViewMode | null): ViewMode { return parseView(search) ?? saved ?? 'iso' }

const subs = new Set<() => void>()
let mode: ViewMode | null = null
export function getViewMode(): ViewMode {
  if (mode === null) mode = initialView(typeof location === 'undefined' ? '' : location.search, read())
  return mode
}
/** Switch view: remembered on this device and written to the address (the page does not reload). */
export function setViewMode(m: ViewMode): void {
  mode = m; write(m)
  try { const s = withView(location.search, m); history.replaceState(history.state, '', `${location.pathname}${s}${location.hash}`) } catch { /* no history */ }
  subs.forEach((f) => f())
}
const subscribe = (f: () => void) => { subs.add(f); return () => { subs.delete(f) } }
export const useViewMode = (): ViewMode => useSyncExternalStore(subscribe, getViewMode, () => 'iso')

/** How high the eye rises on a jump and a double jump (world units; a figure is about 1.2 tall). Working defaults. */
export const JUMP_LIFT = { jump: 0.45, 'double-jump': 0.7 } as const
/**
 * The eye's rise at `now` for the robot's jump state (Jared 11/3: Space jumps in first person). The body is hidden in
 * first person, so the camera itself makes the arc: a parabola over the jump's air time, zero on the ground.
 */
export function jumpLift(s: { act: string | null; actAt: number; airUntil: number }, now: number): number {
  if (now >= s.airUntil || (s.act !== 'jump' && s.act !== 'double-jump')) return 0
  const span = s.airUntil - s.actAt
  if (span <= 0) return 0
  const t = Math.min(1, Math.max(0, (now - s.actAt) / span))
  return JUMP_LIFT[s.act] * 4 * t * (1 - t)
}

/** The drawn weapon in first person, in camera space (x right, y up, -z ahead; world units). Working defaults. */
export const WEAPON_LENGTH = 0.62
export const WEAPON_REST = { x: 0.3, y: -0.3, z: -0.62, pitch: -0.55, yaw: 0.1, roll: 0.35 }
/**
 * The weapon's offset from rest during an attack, `t` from 0 to 1 over the act. Slash sweeps right to left and down,
 * thrust drives straight ahead, flurry is two quick slashes; anything else (or a finished act) is rest.
 */
export function swingPose(act: string | null, t: number): { dx: number; dy: number; dz: number; pitch: number; roll: number } {
  const rest = { dx: 0, dy: 0, dz: 0, pitch: 0, roll: 0 }
  if (!act || !(t >= 0) || t >= 1) return rest
  const arc = Math.sin(Math.PI * t) // out and back
  if (act === 'thrust') return { dx: -0.12 * arc, dy: 0.08 * arc, dz: -0.3 * arc, pitch: -1.0 * arc, roll: -0.2 * arc }
  const k = act === 'flurry' ? (t * 2) % 1 : t
  const a = Math.sin(Math.PI * k)
  return { dx: -0.32 * a, dy: 0.06 * a - 0.1 * k, dz: -0.08 * a, pitch: -0.5 * a, roll: -1.5 * a }
}

/** The orb held up in spell mode (camera space, like WEAPON_REST) and its resting size. Working defaults. */
export const ORB_REST = { x: 0.24, y: -0.2, z: -0.55, size: 0.16 }
/**
 * The orb on a cast, `t` from 0 to 1 over the act: it swells and pushes ahead, more for a charged cast and most for a
 * megacast, then settles. 1 (rest) for anything else.
 */
export function orbPulse(act: string | null, t: number): { scale: number; dz: number; glow: number } {
  const grow = act === 'cast' ? 1.8 : act === 'cast-spell' ? 2.6 : act === 'megacast' ? 3.6 : 0
  if (!grow || !(t >= 0) || t >= 1) return { scale: 1, dz: 0, glow: 1 }
  const a = Math.sin(Math.PI * t)
  return { scale: 1 + (grow - 1) * a, dz: -0.25 * a, glow: 1 + a }
}
